import React, { useState, useEffect } from 'react';

const AdventureStoryPageIllustration = (props) => {
  const pageId = props.pageId || '';
  const illustrationUrl = props.illustrationUrl || '';

  const [loading, setLoading] = useState(!illustrationUrl);
  const [imageUrl, setImageUrl] = useState(illustrationUrl);
  const [error, setError] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const maxPolls = 30; // Maximum number of polling attempts (30 * 2s = 60s max wait)

  // Debug information
  console.log('AdventureStoryPageIllustration props:', { pageId, illustrationUrl });

  // Safely validate pageId to avoid null calls
  const isValidPageId = pageId && !isNaN(parseInt(pageId, 10));

  // Poll for the image if not initially available
  useEffect(() => {
    // If we already have the image, no need to poll
    if (imageUrl) {
      setLoading(false);
      return;
    }

    // Don't poll if page ID is invalid
    if (!isValidPageId) {
      setLoading(false);
      setError(`Invalid page ID: ${pageId}. Cannot load illustration.`);
      console.error('Invalid page ID for AdventureStoryPageIllustration:', pageId);
      return;
    }

    // Don't poll if we've reached the maximum number of attempts
    if (pollingCount >= maxPolls) {
      setLoading(false);
      setError('Image generation is taking longer than expected. Please check back later.');
      return;
    }

    console.log(`Polling for illustration of page ${pageId}, attempt ${pollingCount+1}`);

    // Set up polling if the image isn't available yet
    const pollInterval = setInterval(() => {
      fetch(`/api/v1/adventure_story_pages/${pageId}/check_illustration`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to check illustration status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          console.log('Polling response:', data);
          if (data.illustration_url) {
            // Image is ready
            setImageUrl(data.illustration_url);
            setLoading(false);
            clearInterval(pollInterval);
          } else {
            // Increment polling count
            setPollingCount(prev => prev + 1);
          }
        })
        .catch(err => {
          console.error('Error checking illustration status:', err);
          setError(`Failed to check illustration status: ${err.message}`);
          setLoading(false);
          clearInterval(pollInterval);
        });
    }, 2000); // Poll every 2 seconds

    // Clean up interval on component unmount
    return () => clearInterval(pollInterval);
  }, [imageUrl, pageId, pollingCount, isValidPageId]);

  // Render loading skeleton
  if (loading) {
    return (
      <div className="rounded-lg overflow-hidden bg-gray-100 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-yellow-400 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-gray-600">Creating illustration...</p>
          </div>
        </div>
        <div className="aspect-w-16 aspect-h-9 bg-gray-200 w-full h-64"></div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="rounded-lg overflow-hidden bg-gray-100 p-6 text-center">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  // Render the image
  return (
    <div className="rounded-lg overflow-hidden shadow-lg">
      <img
        src={imageUrl}
        alt="Story illustration"
        className="w-full h-auto"
        loading="lazy"
        onError={() => setError("Failed to load the illustration")}
      />
    </div>
  );
};

export default AdventureStoryPageIllustration;